// AWS Connect Create Client - Import operations for AWS Connect resources

import {
  ConnectClient,
  // List commands for validation
  ListHoursOfOperationsCommand,
  ListAgentStatusesCommand,
  ListQueuesCommand,
  ListQuickConnectsCommand,
  ListRoutingProfilesCommand,
  ListUserHierarchyGroupsCommand,
  DescribeUserHierarchyGroupCommand,
  ListContactFlowsCommand,
  ListUsersCommand,
  ListPhoneNumbersCommand,
  // Create commands (Phase 3)
  CreateHoursOfOperationCommand,
  CreateAgentStatusCommand,
  CreateQueueCommand,
  CreateQuickConnectCommand,
  CreateRoutingProfileCommand,
  CreateUserHierarchyGroupCommand,
  // Update commands (Phase 4)
  UpdateHoursOfOperationCommand,
  UpdateAgentStatusCommand,
  UpdateQueueOutboundCallerConfigCommand,
  UpdateQuickConnectConfigCommand,
  UpdateUserHierarchyGroupNameCommand,
  // Hierarchy structure commands
  DescribeUserHierarchyStructureCommand,
  UpdateUserHierarchyStructureCommand,
  // Associate commands
  AssociateRoutingProfileQueuesCommand
} from '@aws-sdk/client-connect';

import { requestQueueManager } from '../aws/requestQueueManager.js';

/**
 * Create Connect client with credentials
 */
const createConnectClient = (credentials, region) => {
  return new ConnectClient({
    region,
    credentials: {
      accessKeyId: credentials.accessKeyId,
      secretAccessKey: credentials.secretAccessKey,
      sessionToken: credentials.sessionToken,
    },
    retryMode: 'adaptive', // SDK adaptive retry adds a client-side rate limiter as a backstop
    maxAttempts: 2,        // bounded retry; RequestQueueManager's token bucket is the primary pacing
    requestHandler: {
      requestTimeout: 15000,
    }
  });
};

/**
 * List all existing resources in a Connect instance
 * Returns name-to-ID maps for validation and ID resolution
 */
export const listExistingResources = async (credentials, region, instanceId) => {
  const client = createConnectClient(credentials, region);

  const resources = {
    hoursOfOperation: {},
    agentStatus: {},
    queues: {},
    quickConnects: {},
    routingProfiles: {},
    userHierarchyGroups: {},
    contactFlows: {},
    users: {},
    phoneNumbers: {}
  };

  try {
    // List Hours of Operation
    const hoursOfOperationResponse = await requestQueueManager.executeRequest(
      () => client.send(new ListHoursOfOperationsCommand({ InstanceId: instanceId })),
      { resourceType: 'hoursOfOperation' }
    );

    if (hoursOfOperationResponse?.HoursOfOperationSummaryList) {
      hoursOfOperationResponse.HoursOfOperationSummaryList.forEach(item => {
        resources.hoursOfOperation[item.Name] = {
          id: item.Id,
          arn: item.Arn
        };
      });
    }

    // List Agent Statuses
    const agentStatusResponse = await requestQueueManager.executeRequest(
      () => client.send(new ListAgentStatusesCommand({ InstanceId: instanceId })),
      { resourceType: 'agentStatus' }
    );

    if (agentStatusResponse?.AgentStatusSummaryList) {
      agentStatusResponse.AgentStatusSummaryList.forEach(item => {
        resources.agentStatus[item.Name] = {
          id: item.Id,
          arn: item.Arn,
          type: item.Type // ENABLED or DISABLED
        };
      });
    }

    // List Queues - with pagination
    let queuesNextToken = null;
    do {
      const queuesResponse = await requestQueueManager.executeRequest(
        () => client.send(new ListQueuesCommand({
          InstanceId: instanceId,
          NextToken: queuesNextToken
        })),
        { resourceType: 'queues' }
      );

      if (queuesResponse?.QueueSummaryList) {
        queuesResponse.QueueSummaryList.forEach(item => {
          resources.queues[item.Name] = {
            id: item.Id,
            arn: item.Arn,
            queueType: item.QueueType
          };
        });
      }

      queuesNextToken = queuesResponse?.NextToken;
    } while (queuesNextToken);

    // List Quick Connects
    const quickConnectsResponse = await requestQueueManager.executeRequest(
      () => client.send(new ListQuickConnectsCommand({ InstanceId: instanceId })),
      { resourceType: 'quickConnects' }
    );

    if (quickConnectsResponse?.QuickConnectSummaryList) {
      quickConnectsResponse.QuickConnectSummaryList.forEach(item => {
        resources.quickConnects[item.Name] = {
          id: item.Id,
          arn: item.Arn,
          quickConnectType: item.QuickConnectType
        };
      });
    }

    // List Routing Profiles
    const routingProfilesResponse = await requestQueueManager.executeRequest(
      () => client.send(new ListRoutingProfilesCommand({ InstanceId: instanceId })),
      { resourceType: 'routingProfiles' }
    );

    if (routingProfilesResponse?.RoutingProfileSummaryList) {
      routingProfilesResponse.RoutingProfileSummaryList.forEach(item => {
        resources.routingProfiles[item.Name] = {
          id: item.Id,
          arn: item.Arn
        };
      });
    }

    // List User Hierarchy Groups - need to get detailed info for each to build full path
    const hierarchyGroupsResponse = await requestQueueManager.executeRequest(
      () => client.send(new ListUserHierarchyGroupsCommand({ InstanceId: instanceId })),
      { resourceType: 'userHierarchies' }
    );

    if (hierarchyGroupsResponse?.UserHierarchyGroupSummaryList) {
      // Fetch detailed info for each group to get the hierarchy path
      for (const item of hierarchyGroupsResponse.UserHierarchyGroupSummaryList) {
        try {
          const detailResponse = await requestQueueManager.executeRequest(
            () => client.send(new DescribeUserHierarchyGroupCommand({
              InstanceId: instanceId,
              HierarchyGroupId: item.Id
            })),
            { resourceType: 'userHierarchies' }
          );

          const group = detailResponse?.HierarchyGroup;
          if (group) {
            // Build full path key from HierarchyPath (e.g., "Level1Name|Level2Name")
            const pathParts = [];
            const hierarchyPath = group.HierarchyPath;
            if (hierarchyPath?.LevelOne?.Name) pathParts.push(hierarchyPath.LevelOne.Name);
            if (hierarchyPath?.LevelTwo?.Name) pathParts.push(hierarchyPath.LevelTwo.Name);
            if (hierarchyPath?.LevelThree?.Name) pathParts.push(hierarchyPath.LevelThree.Name);
            if (hierarchyPath?.LevelFour?.Name) pathParts.push(hierarchyPath.LevelFour.Name);
            if (hierarchyPath?.LevelFive?.Name) pathParts.push(hierarchyPath.LevelFive.Name);

            const fullPathKey = pathParts.join('|');
            const level = pathParts.length;

            // Store by full path key to handle duplicate names at different levels
            resources.userHierarchyGroups[fullPathKey] = {
              id: group.Id,
              arn: group.Arn,
              name: group.Name,
              level: level,
              hierarchyPath: hierarchyPath
            };

            // Also store by just name for backward compatibility with simple cases
            // But mark it so we know there might be duplicates
            if (!resources.userHierarchyGroupsByName) {
              resources.userHierarchyGroupsByName = {};
            }
            if (!resources.userHierarchyGroupsByName[group.Name]) {
              resources.userHierarchyGroupsByName[group.Name] = [];
            }
            resources.userHierarchyGroupsByName[group.Name].push({
              id: group.Id,
              arn: group.Arn,
              level: level,
              fullPathKey: fullPathKey
            });
          }
        } catch (error) {
          console.warn(`Failed to get details for hierarchy group ${item.Name}:`, error.message);
          // Fall back to basic info using just the name
          resources.userHierarchyGroups[item.Name] = {
            id: item.Id,
            arn: item.Arn
          };
        }
      }
    }

    // List Contact Flows (for reference validation) - with pagination
    let contactFlowsNextToken = null;
    do {
      const contactFlowsResponse = await requestQueueManager.executeRequest(
        () => client.send(new ListContactFlowsCommand({
          InstanceId: instanceId,
          NextToken: contactFlowsNextToken
        })),
        { resourceType: 'contactFlows' }
      );

      if (contactFlowsResponse?.ContactFlowSummaryList) {
        contactFlowsResponse.ContactFlowSummaryList.forEach(item => {
          resources.contactFlows[item.Name] = {
            id: item.Id,
            arn: item.Arn,
            contactFlowType: item.ContactFlowType
          };
        });
      }

      contactFlowsNextToken = contactFlowsResponse?.NextToken;
    } while (contactFlowsNextToken);

    // List Users (for quick connect USER type validation) - with pagination
    let usersNextToken = null;
    do {
      const usersResponse = await requestQueueManager.executeRequest(
        () => client.send(new ListUsersCommand({
          InstanceId: instanceId,
          NextToken: usersNextToken
        })),
        { resourceType: 'users' }
      );

      if (usersResponse?.UserSummaryList) {
        usersResponse.UserSummaryList.forEach(item => {
          resources.users[item.Username] = {
            id: item.Id,
            arn: item.Arn
          };
        });
      }

      usersNextToken = usersResponse?.NextToken;
    } while (usersNextToken);

    // List Phone Numbers (for queue outbound caller ID validation) - with pagination
    let phoneNumbersNextToken = null;
    do {
      const phoneNumbersResponse = await requestQueueManager.executeRequest(
        () => client.send(new ListPhoneNumbersCommand({
          InstanceId: instanceId,
          NextToken: phoneNumbersNextToken
        })),
        { resourceType: 'phoneNumbers' }
      );

      if (phoneNumbersResponse?.PhoneNumberSummaryList) {
        phoneNumbersResponse.PhoneNumberSummaryList.forEach(item => {
          resources.phoneNumbers[item.PhoneNumber] = {
            id: item.PhoneNumberId,
            arn: item.PhoneNumberArn,
            phoneNumberType: item.PhoneNumberType
          };
        });
      }

      phoneNumbersNextToken = phoneNumbersResponse?.NextToken;
    } while (phoneNumbersNextToken);

  } catch (error) {
    console.error('Error listing existing resources:', error);
    throw error;
  }

  return resources;
};

/**
 * Build name-to-ID mapping objects for dependency resolution
 */
export const buildNameToIdMaps = (existingResources) => {
  const maps = {
    hoursOfOperationNameToId: {},
    agentStatusNameToId: {},
    queueNameToId: {},
    quickConnectNameToId: {},
    routingProfileNameToId: {},
    hierarchyGroupNameToId: {},
    contactFlowNameToId: {},
    userNameToId: {},
    phoneNumberToId: {}
  };

  // Build maps
  Object.entries(existingResources.hoursOfOperation || {}).forEach(([name, data]) => {
    maps.hoursOfOperationNameToId[name] = data.id;
  });

  Object.entries(existingResources.agentStatus || {}).forEach(([name, data]) => {
    maps.agentStatusNameToId[name] = data.id;
  });

  Object.entries(existingResources.queues || {}).forEach(([name, data]) => {
    maps.queueNameToId[name] = data.id;
  });

  Object.entries(existingResources.quickConnects || {}).forEach(([name, data]) => {
    maps.quickConnectNameToId[name] = data.id;
  });

  Object.entries(existingResources.routingProfiles || {}).forEach(([name, data]) => {
    maps.routingProfileNameToId[name] = data.id;
  });

  Object.entries(existingResources.userHierarchyGroups || {}).forEach(([name, data]) => {
    maps.hierarchyGroupNameToId[name] = data.id;
  });

  Object.entries(existingResources.contactFlows || {}).forEach(([name, data]) => {
    maps.contactFlowNameToId[name] = data.id;
  });

  Object.entries(existingResources.users || {}).forEach(([name, data]) => {
    maps.userNameToId[name] = data.id;
  });

  // Phone numbers are keyed by the E.164 phone number (e.g., +16282607374)
  Object.entries(existingResources.phoneNumbers || {}).forEach(([phoneNumber, data]) => {
    maps.phoneNumberToId[phoneNumber] = data.id;
  });

  return maps;
};

// ==================== CREATE OPERATIONS (Phase 3) ====================

/**
 * Create Hours of Operation
 */
export const createHoursOfOperation = async (credentials, region, instanceId, data) => {
  const client = createConnectClient(credentials, region);

  const params = {
    InstanceId: instanceId,
    Name: data.Name,
    Description: data.Description || '',
    TimeZone: data.TimeZone,
    Config: data.Config,
    Tags: data.Tags || {}
  };

  const command = new CreateHoursOfOperationCommand(params);
  const response = await requestQueueManager.executeRequest(
    () => client.send(command),
    { resourceType: 'hoursOfOperation' }
  );

  return {
    id: response.HoursOfOperationId,
    arn: response.HoursOfOperationArn
  };
};

/**
 * Create Agent Status
 */
export const createAgentStatus = async (credentials, region, instanceId, data) => {
  const client = createConnectClient(credentials, region);

  const params = {
    InstanceId: instanceId,
    Name: data.Name,
    Description: data.Description || '',
    State: data.State || 'ENABLED',
    DisplayOrder: data.DisplayOrder || 1,
    Tags: data.Tags || {}
  };

  const command = new CreateAgentStatusCommand(params);
  const response = await requestQueueManager.executeRequest(
    () => client.send(command),
    { resourceType: 'agentStatus' }
  );

  return {
    id: response.AgentStatusId,
    arn: response.AgentStatusARN
  };
};

/**
 * Create Queue
 */
export const createQueue = async (credentials, region, instanceId, data) => {
  const client = createConnectClient(credentials, region);

  const params = {
    InstanceId: instanceId,
    Name: data.Name,
    Description: data.Description || '',
    Tags: data.Tags || {}
  };

  // Add optional fields
  if (data.HoursOfOperationId) {
    params.HoursOfOperationId = data.HoursOfOperationId;
  }

  if (data.MaxContacts) {
    params.MaxContacts = data.MaxContacts;
  }

  if (data.OutboundCallerConfig) {
    params.OutboundCallerConfig = data.OutboundCallerConfig;
  }

  const command = new CreateQueueCommand(params);
  const response = await requestQueueManager.executeRequest(
    () => client.send(command),
    { resourceType: 'queues' }
  );

  return {
    id: response.QueueId,
    arn: response.QueueArn
  };
};

/**
 * Create Quick Connect
 */
export const createQuickConnect = async (credentials, region, instanceId, data) => {
  const client = createConnectClient(credentials, region);

  const params = {
    InstanceId: instanceId,
    Name: data.Name,
    Description: data.Description || '',
    QuickConnectConfig: data.QuickConnectConfig,
    Tags: data.Tags || {}
  };

  const command = new CreateQuickConnectCommand(params);
  const response = await requestQueueManager.executeRequest(
    () => client.send(command),
    { resourceType: 'quickConnects' }
  );

  return {
    id: response.QuickConnectId,
    arn: response.QuickConnectARN
  };
};

/**
 * Create Routing Profile
 */
export const createRoutingProfile = async (credentials, region, instanceId, data) => {
  const client = createConnectClient(credentials, region);

  const params = {
    InstanceId: instanceId,
    Name: data.Name,
    Description: data.Description || '',
    DefaultOutboundQueueId: data.DefaultOutboundQueueId,
    MediaConcurrencies: data.MediaConcurrencies,
    Tags: data.Tags || {}
  };

  // Add queue configs if present
  if (data.QueueConfigs && data.QueueConfigs.length > 0) {
    params.QueueConfigs = data.QueueConfigs;
  }

  const command = new CreateRoutingProfileCommand(params);
  const response = await requestQueueManager.executeRequest(
    () => client.send(command),
    { resourceType: 'routingProfiles' }
  );

  return {
    id: response.RoutingProfileId,
    arn: response.RoutingProfileArn
  };
};

/**
 * Create User Hierarchy Group
 */
export const createUserHierarchyGroup = async (credentials, region, instanceId, data) => {
  const client = createConnectClient(credentials, region);

  const params = {
    InstanceId: instanceId,
    Name: data.Name,
    Tags: data.Tags || {}
  };

  // Add parent group ID if this is a child group
  if (data.ParentGroupId) {
    params.ParentGroupId = data.ParentGroupId;
  }

  const command = new CreateUserHierarchyGroupCommand(params);
  const response = await requestQueueManager.executeRequest(
    () => client.send(command),
    { resourceType: 'userHierarchies' }
  );

  return {
    id: response.HierarchyGroupId,
    arn: response.HierarchyGroupArn
  };
};

// ==================== HIERARCHY STRUCTURE OPERATIONS ====================

/**
 * Get current hierarchy structure (level definitions)
 * Returns the level names and ARNs for the instance
 */
export const describeUserHierarchyStructure = async (credentials, region, instanceId) => {
  const client = createConnectClient(credentials, region);

  const command = new DescribeUserHierarchyStructureCommand({
    InstanceId: instanceId
  });

  const response = await requestQueueManager.executeRequest(
    () => client.send(command),
    { resourceType: 'userHierarchies' }
  );

  return response.HierarchyStructure;
};

/**
 * Update hierarchy structure (set level names)
 * Must be called before creating hierarchy groups if the structure doesn't exist
 */
export const updateUserHierarchyStructure = async (credentials, region, instanceId, hierarchyStructure) => {
  const client = createConnectClient(credentials, region);

  const params = {
    InstanceId: instanceId,
    HierarchyStructure: hierarchyStructure // { LevelOne: { Name: '...' }, LevelTwo: { Name: '...' }, ... }
  };

  const command = new UpdateUserHierarchyStructureCommand(params);
  await requestQueueManager.executeRequest(
    () => client.send(command),
    { resourceType: 'userHierarchies' }
  );

  return { success: true };
};

// ==================== UPDATE OPERATIONS (Phase 4) ====================

/**
 * Update Agent Status
 * Note: Can update Name, Description, State, and DisplayOrder
 */
export const updateAgentStatus = async (credentials, region, instanceId, agentStatusId, data) => {
  const client = createConnectClient(credentials, region);

  const params = {
    InstanceId: instanceId,
    AgentStatusId: agentStatusId
  };

  // Add fields to update
  if (data.Name) {
    params.Name = data.Name;
  }

  if (data.Description !== undefined) {
    params.Description = data.Description;
  }

  if (data.State) {
    params.State = data.State;
  }

  if (data.DisplayOrder !== undefined) {
    params.DisplayOrder = data.DisplayOrder;
  }

  const command = new UpdateAgentStatusCommand(params);
  await requestQueueManager.executeRequest(
    () => client.send(command),
    { resourceType: 'agentStatus' }
  );

  return { success: true };
};

/**
 * Update Queue Outbound Caller Config
 * Note: Hours of Operation cannot be updated via this API - requires DeleteQueue + CreateQueue
 */
export const updateQueueOutboundCallerConfig = async (credentials, region, instanceId, queueId, config) => {
  const client = createConnectClient(credentials, region);

  const params = {
    InstanceId: instanceId,
    QueueId: queueId,
    OutboundCallerConfig: config
  };

  const command = new UpdateQueueOutboundCallerConfigCommand(params);
  await requestQueueManager.executeRequest(
    () => client.send(command),
    { resourceType: 'queues' }
  );

  return { success: true };
};

/**
 * Update Quick Connect Config
 */
export const updateQuickConnectConfig = async (credentials, region, instanceId, quickConnectId, data) => {
  const client = createConnectClient(credentials, region);

  const params = {
    InstanceId: instanceId,
    QuickConnectId: quickConnectId,
    QuickConnectConfig: data.QuickConnectConfig
  };

  const command = new UpdateQuickConnectConfigCommand(params);
  await requestQueueManager.executeRequest(
    () => client.send(command),
    { resourceType: 'quickConnects' }
  );

  return { success: true };
};

/**
 * Update Routing Profile Queues
 * Note: This replaces the entire queue configuration
 */
export const updateRoutingProfileQueues = async (credentials, region, instanceId, routingProfileId, queueConfigs) => {
  const client = createConnectClient(credentials, region);

  // Disassociate all existing queues first (not available via API - would need to list first)
  // For now, we'll use AssociateRoutingProfileQueues which adds to existing queues

  const params = {
    InstanceId: instanceId,
    RoutingProfileId: routingProfileId,
    QueueConfigs: queueConfigs
  };

  const command = new AssociateRoutingProfileQueuesCommand(params);
  await requestQueueManager.executeRequest(
    () => client.send(command),
    { resourceType: 'routingProfiles' }
  );

  return { success: true };
};

/**
 * Update User Hierarchy Group Name
 */
export const updateUserHierarchyGroupName = async (credentials, region, instanceId, hierarchyGroupId, name) => {
  const client = createConnectClient(credentials, region);

  const params = {
    InstanceId: instanceId,
    HierarchyGroupId: hierarchyGroupId,
    Name: name
  };

  const command = new UpdateUserHierarchyGroupNameCommand(params);
  await requestQueueManager.executeRequest(
    () => client.send(command),
    { resourceType: 'userHierarchies' }
  );

  return { success: true };
};

/**
 * Update Hours of Operation
 * Updates Name, Description, TimeZone, and Config
 */
export const updateHoursOfOperation = async (credentials, region, instanceId, hoursId, data) => {
  const client = createConnectClient(credentials, region);

  const params = {
    InstanceId: instanceId,
    HoursOfOperationId: hoursId
  };

  // Add fields to update
  if (data.Name) {
    params.Name = data.Name;
  }

  if (data.Description !== undefined) {
    params.Description = data.Description;
  }

  if (data.TimeZone) {
    params.TimeZone = data.TimeZone;
  }

  if (data.Config && data.Config.length > 0) {
    params.Config = data.Config;
  }

  const command = new UpdateHoursOfOperationCommand(params);
  await requestQueueManager.executeRequest(
    () => client.send(command),
    { resourceType: 'hoursOfOperation' }
  );

  return { success: true };
};
